import { flushSync } from 'react-dom'
import type { Locale } from 'shared/types'

const appEnv = typeof window === 'undefined' ? process.env.APP_ENV : window.APP_ENV
export const isDevEnv = appEnv === 'development' || appEnv === 'local'
// A machine running the app from a checkout — `bun dev` and the e2e stack (both APP_ENV=local). NOT the
// dev DEPLOYMENT (danchiano.dev, APP_ENV=development), which `isDevEnv` does include. Gate anything that
// must never reach a deployed environment on this, not on `isDevEnv`.
export const isLocalEnv = appEnv === 'local'

export { cn } from 'cn'

// Cache for Intl.NumberFormat instances to avoid recreating them
const numberFormatCache = new Map<string, Intl.NumberFormat>()
const dateFormatCache = new Map<string, Intl.DateTimeFormat>()

function getNumberFormatter(locale: string, decimalDigits: number): Intl.NumberFormat {
  // Normalize locale for SSR consistency
  const normalizedLocale = locale === 'eu' || locale === 'gl' || locale === 'it' ? 'es' : locale

  const cacheKey = `${normalizedLocale}-${decimalDigits}`

  let formatter = numberFormatCache.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.NumberFormat(normalizedLocale, {
      style: 'decimal',
      minimumFractionDigits: decimalDigits,
      maximumFractionDigits: decimalDigits,
    })
    numberFormatCache.set(cacheKey, formatter)
  }

  return formatter
}

/**
 * Format a number with locale-aware separators.
 * @param decimalDigits - exact number of decimal places (default: 2)
 */
export function formatNumber(locale: string, number: number, decimalDigits = 2) {
  // Fix SSR mismatch error where numbers in Euskera or Galician are formatted in chrome with commas but in node are dots
  return getNumberFormatter(locale, decimalDigits).format(number)
}

// Module-private on purpose: the route-transition folder has its own `prefersReducedMotion` (core/motion.ts)
// which is scoped to that engine and ported verbatim between repos, so this file neither imports it nor
// competes with it by exporting a second one. Both view-transition entry points below read it per call, so a
// user flipping the OS setting mid-session is honoured on the next navigation.
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/**
 * `viewTransition:` for a register/test FLOW navigation — the box morph described in app.css.
 *
 * Off under reduced motion, the same policy (and the same two reasons) as `withViewTransition` below.
 * It has to be decided HERE, at the navigate() call, because the ROUTER is what calls
 * `document.startViewTransition`: the reduced-motion overrides in app.css can only zero the animations WE
 * declare, and the UA's own 250ms `root` cross-fade — which nothing here names — keeps the transition, and
 * therefore the overlay, alive for its full duration regardless.
 *
 * That overlay is not merely decorative: while it is up, a hit test lands on the CAPTURED <html> rather than
 * the element under the pointer, so a press in that window is swallowed. Measured in ranking-sortable.e2e —
 * a press 60-90ms after this navigation reported `pointerdown` on HTML and the drag never started, while the
 * same press 830ms later reported it on the row and worked.
 */
export function flowViewTransition(): boolean {
  return !prefersReducedMotion()
}

// Commit a state update inside a view transition so elements sharing a view-transition-name
// (e.g. indicator titles) morph between layouts. flushSync commits before the snapshot is
// taken; falls back to a plain update where the API is unavailable, or when the user has
// asked for reduced motion. Skipping the animation under prefers-reduced-motion is both an
// accessibility requirement and what keeps it deterministic under test: while the transition
// plays, the ::view-transition overlay intercepts pointer events, so a click landing during
// the (load-dependent) animation can stall — the source of the evaluate-flow flakiness.
export function withViewTransition(update: () => void) {
  if (!prefersReducedMotion() && typeof document !== 'undefined' && 'startViewTransition' in document) {
    document.startViewTransition(() => flushSync(update))
  } else {
    update()
  }
}

export const getIndicatorsColor = (type: string, id: number) => {
  const colourDivitions =
    type === 'comparison' ||
    type === 'competences' ||
    type === 'values' ||
    type === 'personality' ||
    type === 'directives'
      ? ([6, 12, 18] as const)
      : type === 'vectors' || type === 'principles'
        ? ([2, 4, 6] as const)
        : type === 'dimensions' || type === 'scopes'
          ? ([1, 2, 3] as const)
          : []

  return type === 'comparison'
    ? {
        normal: 'var(--color-radar-compare)',
        darker: 'var(--color-radar-compare)',
      }
    : type === 'directives'
      ? {
          normal: 'var(--color-purple)',
          darker: 'var(--color-purple)',
        }
      : type === 'positioning'
        ? {
            normal: 'var(--color-radar-hippie-blue)',
            darker: 'var(--color-radar-hippie-blue-darker)', // Hippie Blue
          }
        : type === 'task' || id < colourDivitions[0]
          ? {
              text: 'var(--color-radar-blue-text)',
              normal: 'var(--color-radar-blue)',
              darker: 'var(--color-radar-blue-darker)',
              // lighter: 'hsl(var(--accent))', // 'var(--color-radar-blue-lightest)',
              lighter: 'var(--color-radar-blue-lightest)',
              // disabled: 'var(--bg-color-2)', // 'var(--color-radar-blue-disabled)',
              disabled: 'var(--color-radar-blue-disabled)',
            }
          : type === 'people' || id < colourDivitions[1]
            ? {
                text: 'var(--color-radar-green-text)',
                normal: 'var(--color-radar-green)',
                darker: 'var(--color-radar-green-darker)',
                // lighter: 'hsl(var(--accent))', // 'var(--color-radar-green-lightest)',
                lighter: 'var(--color-radar-green-lightest)',
                // disabled: 'var(--bg-color-2)', // 'var(--color-radar-green-disabled)',
                disabled: 'var(--color-radar-green-disabled)',
              }
            : type === 'context' || id < colourDivitions[2]
              ? {
                  text: 'var(--color-radar-yellow-text)',
                  normal: 'var(--color-radar-yellow)',
                  darker: 'var(--color-radar-yellow-darker)',
                  // lighter: 'hsl(var(--accent))', // 'var(--color-radar-yellow-lightest)',
                  lighter: 'var(--color-radar-yellow-lightest)',
                  // disabled: 'var(--bg-color-2)', // 'var(--color-radar-yellow-disabled)',
                  disabled: 'var(--color-radar-yellow-disabled)',
                }
              : {
                  normal: 'var(--bg-color-2)',
                  darker: 'var(--bg-color-2)',
                }
}

export const getInitials = (name: string, type: string) => {
  // For team, org_group, and job types, show 2 letter initials
  if (type === 'team' || type === 'org_group' || type === 'job') {
    const nameParts = name.split(' ')
    return nameParts.length > 1
      ? nameParts
          .reduce((previous, current) => `${previous}${current.length > 2 ? current.charAt(0).toUpperCase() : ''}`, '')
          .substring(0, 2)
      : name.substring(0, 2).toUpperCase()
  }

  return name.substring(0, 1).toUpperCase()
}

export const getAgeValue = (
  values: [number, number],
  opts = { showLimits: false },
): { age: string; plural: 'one' | 'other' } =>
  (values[0] >= 0 || values[1] <= 90 || opts.showLimits) && values[0] !== values[1]
    ? { age: `${values[0]} - ${values[1]}`, plural: values[1] === 1 ? 'one' : 'other' }
    : values[0] > 0
      ? { age: values[0].toString(), plural: values[0] === 1 ? 'one' : 'other' }
      : { age: values[1].toString(), plural: values[1] === 1 ? 'one' : 'other' }

export const formatDatetime = (dateString: string | undefined) => {
  if (!dateString) return

  const date = new Date(dateString)

  const year = date.getFullYear().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export const isEmpty = (input?: unknown) => input == null || input === ''

export const basename =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/danchiano') ? '/danchiano' : ''

export const formatDateLocale = (date: string | undefined, locale: Locale) => {
  if (!date) return undefined
  let formatter = dateFormatCache.get(locale)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' })
    dateFormatCache.set(locale, formatter)
  }
  return formatter.format(new Date(date))
}

// Pick an element from a non-empty array using the current truncated timestamp as a seed.
// The seed is stable for ~100 seconds, so the result rotates slowly without re-rendering noise.
export function pickByTime<T>(items: readonly T[]): T | undefined {
  if (items.length === 0) return undefined
  const seed = Date.now().toString().slice(0, -5)
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return items[Math.floor(((h >>> 0) / 4294967296) * items.length)]
}

/**
 * Returns consistent icon styling classes for report section titles
 * Provides both text color and background color classes for better visual hierarchy
 */
export function getSectionIconColors(theme: 'emerald' | 'amber' | 'blue' | 'violet' | 'primary' | 'rose') {
  switch (theme) {
    case 'emerald':
      return {
        text: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-500/10',
      }
    case 'amber':
      return {
        text: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-500/10',
      }
    case 'blue':
      return {
        text: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-500/10',
      }
    case 'violet':
      return {
        text: 'text-violet-600 dark:text-violet-400',
        bg: 'bg-violet-500/10',
      }
    case 'rose':
      return {
        text: 'text-rose-600 dark:text-rose-400',
        bg: 'bg-rose-500/10',
      }
    case 'primary':
      return {
        text: 'text-primary',
        bg: 'bg-primary/10',
      }
  }
}
